import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { nanoid } from "nanoid";
import { format, isValid, parse } from "date-fns";
import { el, ka } from "date-fns/locale";
import _ from "lodash";

import {
  useBlogRating,
  useGetBlogDetails,
  useGetComments,
  usePostComment,
} from "../../apiHooks/details";

import DataContext from "../../context/dataContext";
import PortalContext from "../../context/portalContext";
import Image from "../imageComponent";
import NotFound from "../notFound";

import { ContainerBlock, FlexCenter } from "../../styles";
import { arrRight, fbSvg, linkedInSvg, thumbDown, thumbUp } from "../../svgs";
import {
  BlogWrapper,
  BlogContent,
  Vote,
  CommentsAndLikes,
  WriteComment,
  DecorLine,
} from "./styled";

import CenterSlider from "./centerSlider";
import MultySlider from "./multySlider";
import Comments from "./commentList";
import LoaderCircle from "../loader";
import { useCheckAnalytics } from "../../apiHooks/common";

const BlogDetails: React.FC<any> = () => {
  const { auth, user } = useContext(DataContext);
  const { openLogin } = useContext(PortalContext);

  // eslint-disable-next-line no-empty-pattern
  const {} = useCheckAnalytics(
    {
      action: "blog_details",
      extraReferrer: document.referrer,
    },
    true
  );

  const locales = { ka };

  const location = useLocation();
  const [slug, setSlug] = useState("");

  const getSlugFromPathname = (pathname) => {
    return _.last(pathname.split("/")?.filter(Boolean)) || "";
  };

  // Update slug whenever the pathname changes
  useEffect(() => {
    const newSlug = getSlugFromPathname(location.pathname);
    setSlug(newSlug);
  }, [location.pathname]);

  // Fetch blog details with the current slug
  const { data, isLoading, isSuccess } = useGetBlogDetails(slug);

  const { data: allComments, isError: commentError } = useGetComments(slug);

  // const router = useRouter();
  const [copyed, setCopyed] = useState(false);

  const handleAppear = () => {
    setCopyed(true);
    setTimeout(() => setCopyed(false), 2000);
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    handleAppear();
  };

  // voting
  const { blogRating, loading } = useBlogRating();
  const [type, setType] = useState("");

  const handleVote = (value) => {
    if (!auth) return openLogin();

    const params = {
      like: value === "like" && !data.data.user.like ? true : false,
      dislike: value === "dislike" && !data.data.user.dislike ? true : false,
      user: user.email,
      post: slug,
    };
    blogRating(params);
    setType(value);
    setTimeout(() => {
      setType("");
    }, 500);
  };

  // Write Comment
  const [commenting, setCommenting] = useState(false);
  const [commentVal, setValue] = useState("");
  const commentWrapper = useRef(null);
  const outsideClick = (e) => {
    if (!commentWrapper?.current?.contains(e.target)) setCommenting(false);
  };
  useEffect(() => {
    window.addEventListener("click", outsideClick);
    return () => {
      window.removeEventListener("click", outsideClick);
    };
  }, []);
  const { postComment, loading: commentLoading } = usePostComment();

  const handleComment = (e) => {
    e.preventDefault();
    e.stopPropagation();
    postComment({
      text: commentVal,
      like: 0,
      user: user.email,
      post: slug,
    });
    if (!commentLoading) {
      setCommenting(false);
      setValue("");
    }
  };

  const formatDate = (date: string) => {
    const modifiedDate = parse(date, "MM-dd-yyyy", new Date());
    return format(modifiedDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX", {});
  };

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Article",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": window.location?.origin + location?.pathname,
    },
    headline: data?.data?.title || "blog",
    description: data?.data?.meta_description,
    image: {
      "@type": "ImageObject",
      url: data?.data?.main_image,
      width: "700",
      height: "300",
    },
    author: {
      "@type": "Organization",
      name: data?.data?.author || "Veli Store",
    },
    publisher: {
      "@type": "Organization",
      name: data?.data?.author || "Veli Store",
      logo: {
        "@type": "ImageObject",
        url: "https://veli.store/blog/images/favicon-48.png",
        width: "48",
        height: "48",
      },
    },
    datePublished: data?.data?.record_date
      ? formatDate(data?.data?.record_date)
      : "",
    dateModified: data?.data?.update_date
      ? formatDate(data?.data?.update_date)
      : "",
  };

  return (
    <>
      <BlogWrapper>
        {isLoading ? (
          <div className="loader">
            <LoaderCircle />
          </div>
        ) : isSuccess ? (
          <>
            <ContainerBlock>
              <BlogContent>
                <div className="header">
                  <div className="nav">
                    <Link to="/" className="store">
                      Veli.blog
                    </Link>
                    {data.data?.category.map((el) => {
                      return (
                        <React.Fragment key={nanoid()}>
                          {arrRight()}
                          <Link to={`/?category=${el.slug || el.name}`}>
                            {el.name}
                          </Link>
                        </React.Fragment>
                      );
                    })}
                  </div>

                  <h1 className="headline">{data.data?.title}</h1>

                  <div className="date">
                    {data.data?.minutes} წუთის წასაკითხი &nbsp; &middot; &nbsp;
                    {data.data?.record_date
                      ? isValid(new Date(data.data.record_date))
                        ? format(
                            new Date(data.data.record_date),
                            "d MMMM, yyyy",
                            { locale: locales.ka }
                          )
                        : data.data.record_date
                      : "-"}
                  </div>

                  {/* ??? to do  */}
                  {/* <div className="scores">
                    <span className="views">100 {eyeSvg()}</span>
                    <span className="wall">&#124;</span>
                    <span className="shares">52 {shareSvg()}</span>

                  </div> */}
                </div>

                <div className="hero">
                  <Image src={data.data?.main_image} />
                </div>

                {/* ---- Customize --- */}
                {data.data?.content_data.length ? (
                  <div className="customize">
                    {data.data?.content_data.map((el, i) => {
                      switch (el.type) {
                        case "editor":
                          return (
                            <div
                              className="customize-section"
                              key={nanoid()}
                              dangerouslySetInnerHTML={{
                                __html: el.content.html,
                              }}
                            />
                          );
                        case "slider":
                          return (
                            <div className="customize-section" key={nanoid()}>
                              <CenterSlider slides={el.content} />
                            </div>
                          );
                        case "quote":
                          return (
                            <div className="customize-section" key={nanoid()}>
                              <blockquote>
                                <p>{el.content}</p>
                              </blockquote>
                            </div>
                          );
                        default:
                          return <React.Fragment key={nanoid()} />;
                      }
                    })}
                  </div>
                ) : null}

                {/* _______________________ end of Custom sections   __________________ */}

                <CommentsAndLikes>
                  {/* <div className="social">
                    <button onMouseUp={handleCopy}>
                      <FlexCenter>{linkedInSvg()}</FlexCenter>
                    </button>
                    <button onMouseUp={handleCopy}>
                      <FlexCenter>{fbSvg()}</FlexCenter>
                    </button>
                    <button onMouseUp={handleCopy}>
                      <FlexCenter>{linkedInSvg()}</FlexCenter>
                    </button>
                    <button onMouseUp={handleCopy}>
                      <FlexCenter>{fbSvg()}</FlexCenter>
                    </button>
                  </div> */}

                  {data.data?.tag.length ? (
                    <div className="tags">
                      <h4>თეგები</h4>
                      <div className="flex">
                        {data.data.tag.map((el) => (
                          <Link key={nanoid()} to={`/?tag=${el.slug}`}>
                            {el.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <Vote>
                    <div className="headline">დაგეხმარა სტატია?</div>
                    <div className="actions">
                      <button
                        className={`${data.data?.user.like ? "like" : ""}`}
                        onMouseUp={() => handleVote("like")}
                      >
                        <FlexCenter
                          className={`${type === "like" ? "loader-svg" : ""}`}
                        >
                          {thumbUp()}
                        </FlexCenter>
                        <div className="count">{data.data?.likes} ხმა</div>
                      </button>

                      <button
                        className={`${
                          data.data?.user.dislike ? "dislike" : ""
                        }`}
                        onMouseUp={() => handleVote("dislike")}
                      >
                        <FlexCenter
                          className={`${
                            type === "dislike" ? "loader-svg" : ""
                          }`}
                        >
                          {thumbDown()}
                        </FlexCenter>
                        <div className="count">{data.data?.dislikes} ხმა</div>
                      </button>
                    </div>
                  </Vote>

                  <WriteComment
                    onSubmit={(e) => handleComment(e)}
                    full={commenting}
                    ref={commentWrapper}
                  >
                    <textarea
                      onMouseUp={() => {
                        if (!auth) return openLogin();
                        setCommenting(true);
                      }}
                      readOnly={!auth}
                      name="comment"
                      id=""
                      value={commentVal}
                      placeholder="დაწერე კომენტარი..."
                      onChange={(e) => setValue(e.target.value)}
                    ></textarea>

                    <button className={`${commentVal ? "active" : ""}`}>
                      გაგზავნა
                    </button>
                  </WriteComment>

                  {commentError ? (
                    <span> </span>
                  ) : allComments?.data ? (
                    <Comments comments={allComments.data} slug={slug} />
                  ) : null}
                </CommentsAndLikes>
              </BlogContent>
            </ContainerBlock>

            <DecorLine>
              <div className="first">
                {[...Array(3)].map(() => {
                  return (
                    <React.Fragment key={nanoid()}>
                      <span>
                        გამარჯობა! დროა veli.blog შენს Bookmark bar-ში დაამატო.
                      </span>
                      <span className="dot">•</span>
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="second">
                {[...Array(3)].map(() => {
                  return (
                    <React.Fragment key={nanoid()}>
                      <span>
                        გამარჯობა! დროა veli.blog შენს Bookmark bar-ში დაამატო.
                      </span>
                      <span className="dot">•</span>
                    </React.Fragment>
                  );
                })}
              </div>
            </DecorLine>

            <ContainerBlock>
              {/* recommended products  */}
              {data.data?.content_data.length
                ? data.data.content_data.map((el) => {
                    if (el.type === "product-carousel") {
                      return (
                        <MultySlider
                          key={nanoid()}
                          headline="გირჩევთ პროდუქციას"
                          slides={el.content}
                        />
                      );
                    }
                    return null;
                  })
                : null}

              {/* similar blogs  */}
              {data.data?.similar_posts.length ? (
                <MultySlider
                  blog
                  headline="მსგავსი ბლოგები"
                  slides={data.data.similar_posts}
                />
              ) : null}
            </ContainerBlock>

            {/* schema markup */}
            <script type="application/ld+json">
              {JSON.stringify(productSchema)}
            </script>
          </>
        ) : (
          <NotFound text="ბლოგი ვერ მოიძებნა" />
        )}
      </BlogWrapper>
    </>
  );
};

export default BlogDetails;
